import React, { useState, useEffect } from 'react';
import { complaintService } from '../../services/complaint/complaintService';
import { workOrderService } from '../../services/workOrder/workOrderService';
import { Complaint } from '../../types/complaint';
import { WorkOrder } from '../../types/workOrder';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StatusBadge, PriorityBadge } from '../../components/ui/Badge';
import { LoadingSpinner } from '../../components/ui/Feedback';
import { Wrench, CheckCircle2, Clock, Eye, ClipboardCheck } from 'lucide-react';

interface SupervisorWorkspacePageProps {
  onSelectComplaint: (c: Complaint) => void;
  onSelectWorkOrder?: (wo: WorkOrder) => void;
}

export const SupervisorWorkspacePage: React.FC<SupervisorWorkspacePageProps> = ({ onSelectComplaint, onSelectWorkOrder }) => {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSupervisorData = async () => {
      setIsLoading(true);
      const [complaintRes, woRes] = await Promise.all([
        complaintService.getComplaints(),
        workOrderService.getWorkOrders(),
      ]);
      if (complaintRes.success) setComplaints(complaintRes.data);
      if (woRes.success) setWorkOrders(woRes.data);
      setIsLoading(false);
    };
    fetchSupervisorData();
  }, []);

  const completedForInspection = workOrders.filter(wo => wo.status === 'Completed');
  const activeJobs = workOrders.filter(wo => wo.status === 'In Progress' || wo.status === 'Assigned');
  const inspectedJobs = workOrders.filter(wo => wo.status === 'Inspected' || wo.status === 'Inspection Passed');

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div>
        <h2 className="text-xl font-semibold text-slate-100">Supervisor Field Operations Workspace</h2>
        <p className="text-xs text-slate-400">Monitor assigned complaint resolution, coordinate vendors, and conduct site inspections</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="flex items-center gap-3">
          <div className="p-3 bg-sky-500/10 text-sky-400 rounded-xl">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-slate-400">Active Field Supervision</p>
            <p className="text-2xl font-semibold text-slate-100">{activeJobs.length}</p>
          </div>
        </Card>

        <Card className="flex items-center gap-3">
          <div className="p-3 bg-amber-500/10 text-amber-400 rounded-xl">
            <Wrench className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-slate-400">Ready for Inspection</p>
            <p className="text-2xl font-semibold text-amber-400">{completedForInspection.length}</p>
          </div>
        </Card>

        <Card className="flex items-center gap-3">
          <div className="p-3 bg-emerald-500/10 text-emerald-400 rounded-xl">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-slate-400">Inspected / Closed</p>
            <p className="text-2xl font-semibold text-emerald-400">{inspectedJobs.length}</p>
          </div>
        </Card>
      </div>

      {completedForInspection.length > 0 && (
        <Card title="Ready for Inspection" subtitle="Work orders completed by vendors awaiting supervisor inspection">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {completedForInspection.map(wo => (
              <div key={wo.work_order_id} className="p-4 bg-slate-950 border border-amber-500/20 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-sky-400 font-semibold">{wo.work_order_id}</span>
                  <StatusBadge status={wo.status} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-100 text-sm">{wo.description}</h4>
                  <p className="text-xs text-slate-400 mt-0.5">Vendor: {wo.vendor_name}</p>
                </div>
                <div className="flex items-center justify-between text-xs border-t border-slate-800/80 pt-2 text-slate-400">
                  <span>Target: {wo.target_date}</span>
                  {onSelectWorkOrder ? (
                    <Button size="sm" variant="primary" onClick={() => onSelectWorkOrder(wo)} icon={<ClipboardCheck className="w-3.5 h-3.5" />}>
                      Inspect
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => onSelectComplaint({
                      complaint_id: wo.complaint_id,
                      title: wo.complaint_title,
                      description: '',
                      department_id: '',
                      department_name: '',
                      plant_id: '',
                      building_id: '',
                      location_id: '',
                      location_name: '',
                      category_id: '',
                      category_name: '',
                      priority_name: 'Medium',
                      status_name: 'In Progress',
                      created_by_user_id: '',
                      created_by_user_name: '',
                      created_at: '',
                      attachment_urls: [],
                    })} icon={<Eye className="w-3.5 h-3.5" />}>
                      View
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card title="Assigned Field Complaint Jobs">
        {isLoading ? (
          <LoadingSpinner size="md" text="Loading Supervisor Jobs..." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {complaints.map(c => (
              <div key={c.complaint_id} className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-sky-400 font-semibold">{c.complaint_id}</span>
                  <StatusBadge status={c.status_name} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-100 text-sm">{c.title}</h4>
                  <p className="text-xs text-slate-400 mt-0.5">{c.location_name}</p>
                </div>
                <div className="flex items-center justify-between text-xs border-t border-slate-800/80 pt-2 text-slate-400">
                  <PriorityBadge priority={c.priority_name} />
                  <Button size="sm" variant="ghost" onClick={() => onSelectComplaint(c)} icon={<Eye className="w-3.5 h-3.5" />}>
                    Manage Job
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
